// Packages
import {
  Component,
  Element,
  Event,
  EventEmitter,
  h,
  Host,
  Listen,
  Method,
  Prop,
  State,
  Watch,
} from "@stencil/core";

/**
 * `fx-sideover` is a headless, accessible sliding panel (side-over) component.
 * It surfaces a titled dialog that slides in from a configured edge, supports
 * an optional `fx-backdrop`, and exposes every styleable surface via `part`
 * attributes and state through `data-*` / ARIA attributes so themes can
 * customize it without internal styles.
 *
 * @slot - Default slot for the sideover body content.
 *
 * @part base - The root container that wraps the backdrop and panel.
 * @part backdrop - The `fx-backdrop` overlay rendered when `backdrop` is `true`.
 * @part panel - The dialog panel that slides in from `position`.
 * @part header - The header area containing the title, description, and close button.
 * @part header-text - The wrapper around the title and description.
 * @part title - The sideover title text (set via the `header` prop).
 * @part description - The sideover description text.
 * @part close-button - The close button rendered in the header.
 * @part content - The content area containing the default slot.
 */
// `styleUrl` is intentionally omitted (no internal styles per AGENTS.md).
@Component({
  tag: "fx-sideover",
  shadow: true,
})
export class FxSideover {
  private static nextId = 0;

  // --------------------------------------------
  // Element
  // --------------------------------------------

  @Element() el!: HTMLElement;

  // --------------------------------------------
  // State
  // --------------------------------------------

  /**
   * Tracks the runtime presentation state. `isOpen` drives this state but is
   * never mutated by the component itself (e.g. `dismiss()` does not reset
   * `isOpen` to `false`).
   */
  @State() isPresented = false;

  // --------------------------------------------
  // Props
  // --------------------------------------------

  /**
   * @prop - The sideover header text. Defaults to an empty string, in which
   * case no title element is rendered.
   */
  @Prop() header: string = "";

  /**
   * @prop - The sideover description text. Defaults to an empty string, in
   * which case no description element is rendered.
   */
  @Prop() description: string = "";

  /**
   * @prop - If `true`, the sideover can be dismissed via `dismiss()`, backdrop
   * tap, or the Escape key. Reflects to `data-can-dismiss` on the host.
   */
  @Prop() canDismiss: boolean = true;

  /**
   * @prop - If `true`, a backdrop is rendered behind the panel. Reflects to
   * `data-backdrop` on the host.
   */
  @Prop() backdrop: boolean = true;

  /**
   * @prop - The edge the panel slides in from. Reflects to `data-position` on
   * the host.
   */
  @Prop() position: "left" | "right" | "top" | "bottom" = "right";

  /**
   * @prop - If `true`, the panel is rendered with inset spacing. Reflects to
   * `data-inset` on the host.
   */
  @Prop() inset: boolean = false;

  /**
   * @prop - If `true`, tapping the backdrop dismisses the sideover. Reflects to
   * `data-backdrop-dismiss` on the host.
   */
  @Prop() backdropDismiss: boolean = true;

  /**
   * @prop - Controls whether the sideover is presented. This prop is read by
   * the component and is never auto-reset when the sideover is dismissed.
   */
  @Prop() isOpen: boolean = false;

  // --------------------------------------------
  // Events
  // --------------------------------------------

  /**
   * @event - Emitted before the sideover begins presenting.
   */
  @Event() fxWillPresent!: EventEmitter<void>;

  /**
   * @event - Emitted after the sideover has finished presenting.
   */
  @Event() fxDidPresent!: EventEmitter<void>;

  /**
   * @event - Emitted before the sideover begins dismissing.
   */
  @Event() fxWillDismiss!: EventEmitter<void>;

  /**
   * @event - Emitted after the sideover has finished dismissing.
   */
  @Event() fxDidDismiss!: EventEmitter<void>;

  // --------------------------------------------
  // Lifecycle
  // --------------------------------------------

  async componentWillLoad() {
    if (this.isOpen) {
      await this.present();
    }
  }

  @Watch("isOpen")
  onIsOpenChange(open: boolean) {
    if (open) {
      this.present();
    } else {
      this.dismiss();
    }
  }

  // --------------------------------------------
  // Keyboard
  // --------------------------------------------

  @Listen("keydown", { target: "window" })
  handleKeyDown(ev: KeyboardEvent) {
    if (ev.key === "Escape" && this.isPresented && this.canDismiss) {
      ev.preventDefault();
      this.dismiss();
    }
  }

  // --------------------------------------------
  // Methods
  // --------------------------------------------

  /**
   * @method - Presents the sideover.
   */
  @Method()
  async present(): Promise<void> {
    if (this.isPresented) {
      return;
    }

    this.previousActiveElement = this.el.ownerDocument?.activeElement ?? null;
    this.fxWillPresent.emit();
    this.isPresented = true;
    this.afterPresent();
  }

  /**
   * @method - Dismisses the sideover if `canDismiss` is `true`. Returns `true`
   * when the sideover was dismissed, `false` when it was prevented.
   */
  @Method()
  async dismiss(): Promise<boolean> {
    if (!this.isPresented) {
      return true;
    }
    if (!this.canDismiss) {
      return false;
    }

    this.fxWillDismiss.emit();
    this.isPresented = false;
    this.afterDismiss();
    return true;
  }

  // --------------------------------------------
  // Private helpers
  // --------------------------------------------

  private readonly sideoverId = ++FxSideover.nextId;
  private panelEl: HTMLElement | undefined;
  private previousActiveElement: Element | null = null;
  private rafId: number | undefined;
  private rafWin: Window | undefined;

  private handleBackdropTap = async () => {
    if (this.backdropDismiss) {
      await this.dismiss();
    }
  };

  private handleCloseClick = async () => {
    await this.dismiss();
  };

  private afterPresent() {
    this.raf(() => {
      this.fxDidPresent.emit();
      this.focusPanel();
    });
  }

  private afterDismiss() {
    this.raf(() => {
      this.fxDidDismiss.emit();
      this.restoreFocus();
    });
  }

  disconnectedCallback() {
    this.cancelRaf();
  }

  private cancelRaf() {
    if (this.rafId !== undefined && this.rafWin) {
      if (typeof this.rafWin.cancelAnimationFrame === "function") {
        this.rafWin.cancelAnimationFrame(this.rafId);
      } else {
        clearTimeout(this.rafId);
      }
      this.rafId = undefined;
      this.rafWin = undefined;
    }
  }

  private raf(callback: FrameRequestCallback) {
    this.cancelRaf();
    const win = this.el?.ownerDocument?.defaultView;
    if (win && typeof win.requestAnimationFrame === "function") {
      this.rafWin = win;
      this.rafId = win.requestAnimationFrame(callback);
    } else {
      this.rafWin = undefined;
      this.rafId = window.setTimeout(callback, 0);
    }
  }

  private focusPanel() {
    if (this.panelEl && typeof this.panelEl.focus === "function") {
      this.panelEl.focus();
    }
  }

  private restoreFocus() {
    const previous = this.previousActiveElement as HTMLElement | null;
    if (previous && typeof previous.focus === "function") {
      previous.focus();
    }
  }

  private get headerId(): string {
    return `fx-sideover-title-${this.sideoverId}`;
  }

  private get descriptionId(): string {
    return `fx-sideover-description-${this.sideoverId}`;
  }

  // --------------------------------------------
  // Render
  // --------------------------------------------

  render() {
    const {
      header,
      description,
      canDismiss,
      backdrop,
      position,
      inset,
      isPresented,
    } = this;
    const hasHeader = header !== "";
    const hasDescription = description !== "";

    return (
      <Host
        data-is-open={isPresented ? "true" : "false"}
        data-position={position}
        data-inset={inset ? "" : undefined}
        data-backdrop={backdrop ? "true" : "false"}
        data-can-dismiss={canDismiss ? "true" : "false"}
        data-backdrop-dismiss={this.backdropDismiss ? "true" : "false"}
        aria-hidden={!isPresented ? "true" : null}
        exportparts="backdrop"
      >
        <div part="base">
          {backdrop && (
            <fx-backdrop
              visible={isPresented}
              tappable={this.backdropDismiss}
              stopPropagation={true}
              onFxBackdropTap={this.handleBackdropTap}
              exportparts="backdrop"
            />
          )}

          <div
            part="panel"
            role="dialog"
            aria-modal="true"
            aria-label={header || "Sideover"}
            aria-labelledby={hasHeader ? this.headerId : null}
            aria-describedby={hasDescription ? this.descriptionId : null}
            tabIndex={-1}
            ref={(el) => (this.panelEl = el as HTMLElement)}
          >
            {(hasHeader || hasDescription) && (
              <div part="header">
                <div part="header-text">
                  {hasHeader && (
                    <div part="title" id={this.headerId}>
                      {header}
                    </div>
                  )}
                  {hasDescription && (
                    <div part="description" id={this.descriptionId}>
                      {description}
                    </div>
                  )}
                </div>
                {canDismiss && (
                  <button
                    part="close-button"
                    type="button"
                    aria-label="Close"
                    onClick={this.handleCloseClick}
                  >
                    ×
                  </button>
                )}
              </div>
            )}

            <div part="content">
              <slot />
            </div>
          </div>
        </div>
      </Host>
    );
  }
}
